"use client";

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Loader2, FileCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/HoverCard";
import {
  loadMiscSignoffs,
  addMiscSignoffs,
  getSignoffEntriesForCard,
  type MiscSignoffEntry,
  type MiscSignoffType,
} from "../../utils/misc-signoff-utils";

const SIGNOFF_LABELS: Record<MiscSignoffType, string> = {
  prep_so: "Preparer",
  rev_so: "Reviewer",
  conc_so: "Concurrent",
  eqr_so: "EQR",
  partner_so: "Partner",
};

/** Compact labels for token display on cards (match substantive procedure cards) */
const SIGNOFF_TOKEN_LABELS: Record<MiscSignoffType, string> = {
  prep_so: "P",
  rev_so: "R",
  conc_so: "CR",
  eqr_so: "EQR",
  partner_so: "EP",
};

function getInitials(name: string): string {
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function formatSignoffDateTime(value: string): string {
  if (!value) return "";
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return value;
    return dt.toLocaleString();
  } catch {
    return value;
  }
}

/** Display sign-off tokens on planning cards (same style as procedure cards). Use className e.g. "mt-0" for inline next to button. */
export function PlanningSignoffTokens({ signoffs, className }: { signoffs?: MiscSignoffEntry[]; className?: string }) {
  if (!signoffs || signoffs.length === 0) return null;
  const byType = (["prep_so", "rev_so", "conc_so", "eqr_so", "partner_so"] as MiscSignoffType[]).map((type) => ({
    type,
    label: SIGNOFF_TOKEN_LABELS[type],
    entries: signoffs.filter((e) => e.type === type),
  }));
  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] ${className ?? "mt-2"}`}>
      {byType.map(
        ({ type, label, entries }) =>
          entries.length > 0 && (
            <HoverCard key={type} openDelay={150} closeDelay={100}>
              <HoverCardTrigger asChild>
                <div className="flex items-center gap-0.5 cursor-help">
                  <span className="text-white/50 font-medium shrink-0">{label}:</span>
                  <div className="flex items-center gap-0.5">
                    {entries.slice(0, 3).map((e, i) => (
                      <span
                        key={i}
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/15 text-white/90 border border-white/20 font-medium"
                        title={`${e.name}${e.metadata ? ` - ${formatSignoffDateTime(e.metadata)}` : ""}`}
                      >
                        {getInitials(e.name)}
                      </span>
                    ))}
                    {entries.length > 3 && (
                      <span className="text-white/50">+{entries.length - 3}</span>
                    )}
                  </div>
                </div>
              </HoverCardTrigger>
              <HoverCardContent className="w-72 p-3">
                <div className="text-xs font-semibold text-white/90 mb-2">
                  {SIGNOFF_LABELS[type]} ({entries.length})
                </div>
                <div className="space-y-1.5 max-h-52 overflow-y-auto">
                  {entries.map((entry, idx) => (
                    <div key={idx} className="border-b border-white/10 pb-1 last:border-0 last:pb-0">
                      <div className="text-xs text-white/90">{entry.name}</div>
                      {entry.metadata && (
                        <div className="text-[10px] text-white/60">{formatSignoffDateTime(entry.metadata)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </HoverCardContent>
            </HoverCard>
          )
      )}
    </div>
  );
}

function canSignoffSectionByRole(
  section: MiscSignoffType,
  user: { type?: string } | null,
  engagementRole: string | null
): boolean {
  if (!user) return false;
  const userType = user.type;

  switch (section) {
    case "prep_so":
      return (
        engagementRole === "associate" ||
        engagementRole === "article" ||
        userType === "associate" ||
        userType === "article"
      );
    case "rev_so":
      return (
        engagementRole === "engagement_manager" ||
        userType === "manager" ||
        userType === "associate" ||
        engagementRole === "associate"
      );
    case "conc_so":
      return userType === "manager" || userType === "partner";
    case "eqr_so":
      return engagementRole === "eqr_partner" || engagementRole === "eqr_manager";
    case "partner_so":
      return engagementRole === "engagement_partner";
    default:
      return false;
  }
}

function isCardPrepCompleted(signoffs: MiscSignoffEntry[] | undefined): boolean {
  if (!signoffs || signoffs.length === 0) return false;
  return signoffs.some((e) => e.type === "prep_so" && e?.name && String(e.name).trim() !== "");
}

interface PlanningSignoffDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceName: string;
  cardNames: string[];
  user: { user_name?: string; type?: string } | null;
  userEngagementRole: string | null;
  onSignoffDone?: () => void;
}

export default function PlanningSignoffDialog({
  isOpen,
  onClose,
  workspaceName,
  cardNames,
  user,
  userEngagementRole,
  onSignoffDone,
}: PlanningSignoffDialogProps) {
  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());
  const [signingOff, setSigningOff] = useState(false);
  const [error, setError] = useState<string>("");
  const [cardSignoffsMap, setCardSignoffsMap] = useState<Record<string, MiscSignoffEntry[]>>({});
  const [loadingSignoffs, setLoadingSignoffs] = useState(false);

  useEffect(() => {
    if (!isOpen || !workspaceName) return;
    const load = async () => {
      setLoadingSignoffs(true);
      try {
        const data = await loadMiscSignoffs();
        const map: Record<string, MiscSignoffEntry[]> = {};
        cardNames.forEach((name) => {
          map[name] = getSignoffEntriesForCard(workspaceName, name, data.signoff);
        });
        setCardSignoffsMap(map);
      } catch {
        setCardSignoffsMap({});
      } finally {
        setLoadingSignoffs(false);
      }
    };
    load();
  }, [isOpen, workspaceName, cardNames.join(",")]);

  const allSelectedHavePreparer =
    selectedCards.size > 0 &&
    Array.from(selectedCards).every((name) => isCardPrepCompleted(cardSignoffsMap[name]));

  const toggleCard = (name: string) => {
    setSelectedCards((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedCards.size === cardNames.length) {
      setSelectedCards(new Set());
    } else {
      setSelectedCards(new Set(cardNames));
    }
  };

  const handleSignoff = async (section: MiscSignoffType) => {
    if (!user?.user_name || selectedCards.size === 0) return;
    setError("");
    setSigningOff(true);
    try {
      const result = await addMiscSignoffs(
        workspaceName,
        Array.from(selectedCards),
        section,
        user.user_name
      );
      if (result.success) {
        onSignoffDone?.();
        onClose();
      } else {
        setError(result.error || "Failed to save sign-off");
      }
    } catch (e: any) {
      setError(e?.message || "Failed to save sign-off");
    } finally {
      setSigningOff(false);
    }
  };

  const sections: MiscSignoffType[] = ["prep_so", "rev_so", "conc_so", "eqr_so", "partner_so"];

  const canSignoffSection = (section: MiscSignoffType): boolean => {
    const byRole = canSignoffSectionByRole(section, user, userEngagementRole);
    if (section === "prep_so") return byRole;
    return byRole && allSelectedHavePreparer;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md border-white/10 bg-gray-900 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <FileCheck className="h-5 w-5 text-amber-400" />
            Sign off cards
          </DialogTitle>
          <DialogDescription className="text-white/70">
            Select one or more cards, then choose the sign-off type. Rules and roles are the same as in other libraries.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {cardNames.length === 0 ? (
            <p className="text-sm text-white/60">No cards in this workspace.</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                  <Checkbox
                    checked={cardNames.length > 0 && selectedCards.size === cardNames.length}
                    onCheckedChange={selectAll}
                    className="border-white/30 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                  />
                  Select all
                </label>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2 rounded-md border border-white/10 bg-black/20 p-2">
                {cardNames.map((name) => (
                  <label
                    key={name}
                    className="flex items-center gap-2 text-sm text-white cursor-pointer hover:bg-white/5 rounded px-2 py-1.5"
                  >
                    <Checkbox
                      checked={selectedCards.has(name)}
                      onCheckedChange={() => toggleCard(name)}
                      className="border-white/30 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    />
                    {name}
                  </label>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-2">
                <span className="text-sm text-white/70">Sign off as:</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={
                        selectedCards.size === 0 ||
                        signingOff ||
                        !user?.user_name ||
                        loadingSignoffs
                      }
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      {signingOff || loadingSignoffs ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Choose type"
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-gray-900 border-white/10">
                    {sections.map((section) => {
                      const allowed = canSignoffSection(section);
                      const needsPrep = section !== "prep_so" && !allSelectedHavePreparer;
                      return (
                        <DropdownMenuItem
                          key={section}
                          disabled={!allowed}
                          onClick={() => allowed && handleSignoff(section)}
                          className="text-white focus:bg-white/10 focus:text-white"
                          title={
                            needsPrep
                              ? "Preparer sign-off required on all selected cards first"
                              : undefined
                          }
                        >
                          {SIGNOFF_LABELS[section]}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {selectedCards.size > 0 && !allSelectedHavePreparer && (
                <p className="text-xs text-amber-400/90">
                  Reviewer, Concurrent, EQR and Partner sign-off require Preparer sign-off on all selected cards first.
                </p>
              )}
            </>
          )}

          {error && (
            <p className="text-sm text-red-400">{error}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <Button variant="outline" onClick={onClose} className="border-white/20 text-white hover:bg-white/10">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
